"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const ABBREVIATIONS = [
  "Dr.",
  "Mr.",
  "Mrs.",
  "Ms.",
  "Prof.",
  "St.",
  "Jr.",
  "Sr.",
  "e.g.",
  "i.e.",
  "vs.",
  "cf.",
  "Fig.",
  "Vol.",
  "Ed.",
  "pp.",
  "U.S.",
  "U.K.",
  "a.m.",
  "p.m.",
];

const PLACEHOLDER = "";
const PARAGRAPH_BREAK_RE = /\n[^\S\n]*\n\s*/;
/*
 * "etc." and "No." end a sentence when a capitalised word follows ("…milk, etc. Then they
 * left." / "Is it right? No. I think not."), and stay put only before a number ("No. 5").
 */
const ABBREVIATION_BEFORE_NUMBER_RE = /\b(etc|No)\.(?=\s+\d)/g;
/*
 * Initials: a lone capital and a full stop that begins a chain ("J. K. Rowling") or sits
 * before a name ("J. Smith"). "I" is the pronoun and ends sentences; the words that
 * commonly open a sentence mean the capital was a label ("Plan B. Then we go.").
 */
const INITIAL_CHAIN_RE = /(?<![\p{L}\p{N}.])([A-HJ-Z])\.(?=\s+[A-Z]\.)/gu;
const INITIAL_BEFORE_NAME_RE =
  /(?<![\p{L}\p{N}.])([A-HJ-Z])\.(?=\s+([A-Z][\p{L}'’-]+))/gu;
const SENTENCE_OPENERS = new Set([
  "A",
  "An",
  "And",
  "As",
  "At",
  "But",
  "Do",
  "Does",
  "Did",
  "For",
  "He",
  "Her",
  "His",
  "How",
  "If",
  "In",
  "Is",
  "It",
  "Its",
  "My",
  "No",
  "Not",
  "Now",
  "On",
  "Our",
  "She",
  "So",
  "That",
  "The",
  "Their",
  "Then",
  "There",
  "These",
  "They",
  "This",
  "Those",
  "We",
  "What",
  "When",
  "Where",
  "Who",
  "Why",
  "Yes",
  "You",
]);
const DECIMAL_RE = /(\d)\.(\d)/g;
const WHITESPACE_RE = /\s+/g;
const SENTENCE_BREAK_RE = /([.!?]+)(["'”’)\]]*)\s+(?=["'“‘([]*[A-Z0-9])/g;

/** English strings. A translated content file overrides these through `ui`. */
export const SENTENCE_SPLITTER_STRINGS = {
  inputText: "Input text",
  placeholder:
    "Paste a paragraph here. Dr. Smith arrived at 3 p.m. The value was 3.14. Was it correct?",
  charactersCount: "one:{count} character|other:{count} characters",
  splitSentences: "Split Sentences",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  oneSentencePerLine: "One sentence per line",
  sentences: "Sentences",
  inputCharacters: "Input characters",
  outputCharacters: "Output characters",
} as const;

function protectAbbreviations(text: string): string {
  let result = text;
  for (const abbr of ABBREVIATIONS) {
    result = result.replaceAll(abbr, abbr.replaceAll(".", PLACEHOLDER));
  }
  return result;
}

function protectInitials(text: string): string {
  const chained = text.replace(INITIAL_CHAIN_RE, `$1${PLACEHOLDER}`);
  return chained.replace(
    INITIAL_BEFORE_NAME_RE,
    (match, letter: string, name: string) =>
      SENTENCE_OPENERS.has(name) ? match : `${letter}${PLACEHOLDER}`
  );
}

export function splitSentences(text: string): string[] {
  let working = text.replace(WHITESPACE_RE, " ").trim();
  working = protectAbbreviations(working);
  working = working.replace(ABBREVIATION_BEFORE_NUMBER_RE, `$1${PLACEHOLDER}`);
  working = protectInitials(working);
  working = working.replace(DECIMAL_RE, `$1${PLACEHOLDER}$2`);
  working = working.replace(SENTENCE_BREAK_RE, "$1$2\n");
  return working
    .split("\n")
    .map((s) => s.replaceAll(PLACEHOLDER, ".").trim())
    .filter((s) => s.length > 0);
}

/** Sentences per paragraph: a blank line in the input starts a new paragraph. */
export function splitParagraphs(text: string): string[][] {
  return text
    .split(PARAGRAPH_BREAK_RE)
    .map(splitSentences)
    .filter((sentences) => sentences.length > 0);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function SentenceSplitterWidget() {
  const strings = useToolUi(SENTENCE_SPLITTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [sentenceCount, setSentenceCount] = useState(0);
  const [copied, setCopied] = useState(false);

  function handleSplit() {
    if (input.trim().length === 0) {
      return;
    }
    const paragraphs = splitParagraphs(input);
    setOutput(paragraphs.map((p) => p.join("\n")).join("\n\n"));
    setSentenceCount(paragraphs.reduce((sum, p) => sum + p.length, 0));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setSentenceCount(0);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="split-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="split-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.charactersCount, locale, input.length, {
            count: fmt(input.length),
          })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleSplit}
          type="button"
        >
          {strings.splitSentences}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="split-output"
          >
            {strings.oneSentencePerLine}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="split-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.sentences} value={fmt(sentenceCount)} />
          <StatCard label={strings.inputCharacters} value={fmt(input.length)} />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(output.length)}
          />
        </div>
      )}
    </>
  );
}
